<template>
  <div class="image-upload-manager">
    <!-- Image thumbnails -->
    <v-row v-if="images.length > 0" class="ma-0 pa-0 mb-2">
      <v-col cols="12" class="ma-0 pa-2">
        <div class="image-thumbnails-container">
          <div 
            v-for="(image, index) in images" 
            :key="`img-${index}`" 
            class="image-thumbnail-wrapper"
          >
            <img 
              :src="image.preview" 
              :alt="image.fileName" 
              class="image-thumbnail" 
            />
            <v-btn
              icon="mdi-close-circle"
              size="x-small"
              class="delete-image-btn"
              @click="removeImage(index)"
              variant="text"
              color="error"
              :disabled="disabled"
            />
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Hidden file input -->
    <input
      ref="hiddenFileInput"
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      multiple
      style="display: none"
      @change="handleFileInputChange"
      :disabled="disabled"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { UploadedImage, processImageFiles } from '@/utils/imageHandler'

@Options({
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    maxImages: {
      type: Number,
      default: 10
    }
  },
  emits: ['images-changed', 'error']
})
export default class ImageUploadManager extends Vue {
  disabled!: boolean
  maxImages!: number
  images: UploadedImage[] = []
  hiddenFileInput: HTMLInputElement | null = null

  mounted() {
    this.hiddenFileInput = this.$refs.hiddenFileInput as HTMLInputElement
  }

  // Public method to trigger file selection
  selectImages(): void {
    if (this.hiddenFileInput && !this.disabled) {
      this.hiddenFileInput.click()
    }
  }

  async handleFileInputChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return

    await this.addFiles(Array.from(input.files))

    // Reset input so the same file can be picked again later
    input.value = ''
  }

  // Public method: add File objects from any source (file picker, clipboard
  // paste, drag-and-drop). Respects maxImages and emits the same events as
  // the file picker flow so consumers don't need to distinguish sources.
  async addFiles(files: File[]): Promise<void> {
    if (files.length === 0) return

    const remainingSlots = this.maxImages - this.images.length
    if (remainingSlots <= 0) {
      this.$emit('error', `Maximum ${this.maxImages} images allowed`)
      return
    }

    // Surface partial truncation too, so a user pasting 3 images with only
    // 1 slot left sees the same cap message instead of silently losing two.
    if (files.length > remainingSlots) {
      this.$emit('error', `Maximum ${this.maxImages} images allowed`)
    }

    const filesToProcess = files.slice(0, remainingSlots)
    const { images, errors } = await processImageFiles(filesToProcess)

    this.images.push(...images)

    errors.forEach(error => this.$emit('error', error))

    this.$emit('images-changed', [...this.images])
  }

  removeImage(index: number): void {
    this.images.splice(index, 1)
    this.$emit('images-changed', [...this.images])
  }

  // Public method to clear all images
  clearImages(): void {
    this.images = []
    this.$emit('images-changed', [])
  }

  // Public getter for current images
  getImages(): UploadedImage[] {
    return [...this.images]
  }
}
</script>

<style lang="scss" scoped>
.image-thumbnails-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px;
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
  border-radius: 4px;
  max-height: 120px;
  overflow-y: auto;
}

.image-thumbnail-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
}

.image-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid rgba(var(--v-theme-primary), 0.3);
}

.delete-image-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 50%;
}
</style>
